// @flow
import React, { useRef, useState, useEffect, useCallback } from 'react';
// Components
import Slider from 'react-slick';
import { Link } from 'react-router-dom';
// Analytics
import * as Analytics from '../../common/analytics';
// Styles
import './ImageCarousel.css';

type Props = {
  images: {
    id: number,
    src: string,
    link: string,
    alt: string,
  }[],
  isLoading?: boolean,
};

const ImageCarousel = (props: Props) => {
  const sliderRef = useRef();
  const [currentSlide, setCurrentSlide] = useState(0);

  const progressRefs = useRef(new Map());
  const animationFrameId = useRef(null);

  const startProgress = useCallback(
    (durationMs, selectedIndex) => {
      cancelAnimationFrame(animationFrameId.current);

      const node = progressRefs.current.get(selectedIndex);
      if (!node) return;

      progressRefs.current.forEach((node, index) => {
        if (index !== selectedIndex) {
          node.style.width = '0%';
        }
      });

      let start = null;

      const animate = timestamp => {
        if (!start) start = timestamp;
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / durationMs, 1);

        node.style.width = `${progress * 100}%`;

        if (progress < 1) {
          animationFrameId.current = requestAnimationFrame(animate);
        } else {
          const nextIndex = (selectedIndex + 1) % props.images.length;
          sliderRef.current.slickGoTo(nextIndex);
        }
      };

      animationFrameId.current = requestAnimationFrame(animate);
    },
    [props.images.length],
  );

  const settings = {
    dots: false,
    infinite: true,
    autoplay: false,
    autoplaySpeed: 6000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    swipe: true,
    vertical: false,
    lazyLoad: true, // Loads one image at a time starting with the slide that appears first
    afterChange: nextIndex => {
      setCurrentSlide(nextIndex);
      startProgress(settings.autoplaySpeed, nextIndex);
    },
  };

  useEffect(() => {
    if (props.isLoading || !props.images.length) {
      return;
    }
    startProgress(settings.autoplaySpeed, currentSlide);
    return () => {
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [currentSlide, props.isLoading, props.images.length]);

  const handleProgressClick = useCallback(
    ind => {
      if (ind === currentSlide) {
        startProgress(settings.autoplaySpeed, ind);
      } else {
        sliderRef.current.slickGoTo(ind);
      }
    },
    [currentSlide, startProgress, settings.autoplaySpeed],
  );

  if (props.isLoading) {
    return (
      <div className="image-carousel-wrapper">
        <div className="image-carousel-skeleton skeleton-block" />
        <div className="carousel-buttons">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="carousel-button carousel-button--skeleton skeleton-block"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="image-carousel-wrapper">
      <div className="image-carousel-slider">
        <Slider ref={sliderRef} {...settings}>
          {props.images.map((img, ind) => {
            const isExternalLink = img.link.includes('https');
            if (isExternalLink) {
              return (
                <a
                  href={img.link}
                  key={ind}
                  target="_blank"
                  data-index={ind}
                  onClick={() => Analytics.logTappedBanner(img.alt)}
                  rel="noreferrer noopener"
                >
                  <img
                    className="image-carousel-img"
                    src={img.src}
                    alt={img.alt}
                  />
                </a>
              );
            }
            return (
              <Link
                to={img.link}
                key={ind}
                data-index={ind}
                onClick={() => Analytics.logTappedBanner(img.alt)}
              >
                <img
                  className="image-carousel-img"
                  src={img.src}
                  alt={img.alt}
                />
              </Link>
            );
          })}
        </Slider>
      </div>

      <div className="carousel-buttons">
        {props.images.map((image, ind) => (
          <button
            key={image.id}
            className={`carousel-button${currentSlide > ind ? ' active' : ''}`}
            onClick={() => handleProgressClick(ind)}
          >
            <div
              className={currentSlide === ind && 'carousel-progress-bar'}
              ref={el => {
                progressRefs.current.set(ind, el);
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
